import { Activity, RollupConsiderationsConfig } from "../activity";
import { CompletionStatus, SuccessStatus } from "../../../../constants/enums";

/**
 * Type of rollup operation being performed
 */
export type RollupType = "measure" | "objective" | "progress";

/**
 * Rollup action for consideration filtering
 */
export type RollupAction = "satisfied" | "notSatisfied" | "completed" | "incomplete";

/**
 * RollupChildFilter - Handles child activity filtering for rollup operations
 * Implements SCORM 2004 RB.1.4.2 (Check Child For Rollup Subprocess)
 *
 * This class is responsible for determining which child activities should
 * contribute to rollup calculations based on their tracking settings and
 * rollup consideration requirements.
 *
 * @spec SN Book: RB.1.4.2 (Check Child For Rollup Subprocess)
 */
export class RollupChildFilter {
  /**
   * Check Child For Rollup Subprocess
   * Determines if a child activity contributes to rollup based on its individual consideration settings
   * This implements the full SCORM 2004 RB.1.4.2 specification
   *
   * @spec SN Book: RB.1.4.2 (Check Child For Rollup Subprocess)
   * @param child - The child activity to check
   * @param rollupType - Type of rollup ("measure", "objective", "progress")
   * @param rollupAction - Specific rollup action (satisfied, notSatisfied, completed, incomplete)
   * @returns True if child contributes to rollup
   */
  public checkChildForRollupSubprocess(
    child: Activity,
    rollupType: RollupType,
    rollupAction?: RollupAction,
  ): boolean {
    // First check if child is tracked
    if (child.sequencingControls.tracked === false) {
      return false;
    }

    // Current-attempt controls affect whether the parent may use a child's data for rollup; they
    // do not erase the child's tracking state, which remains available to the child's own rules.
    // @spec SCORM 2004 SN 4th Ed. SM.1 useCurrentAttemptObjectiveInfo and
    // useCurrentAttemptProgressInfo
    if (rollupType === "measure" && child.objectiveInfoAvailableInCurrentParentAttempt === false) {
      return false;
    }

    let included = false;

    // RB.1.4.2 Step 2: Check for objective rollup (satisfied/notSatisfied)
    if (rollupType === "measure" || rollupType === "objective") {
      // Step 2.1: Check if child tracks objective rollup
      if (!child.sequencingControls.rollupObjectiveSatisfied) {
        return false;
      }

      // Step 2.1.1: Default to included
      included = true;

      // Get the child's individual consideration requirements
      const requiredForSatisfied = child.requiredForSatisfied;
      const requiredForNotSatisfied = child.requiredForNotSatisfied;

      // Step 2.1.2: Check ifNotSuspended consideration
      if (
        (rollupAction === "satisfied" && requiredForSatisfied === "ifNotSuspended") ||
        (rollupAction === "notSatisfied" && requiredForNotSatisfied === "ifNotSuspended")
      ) {
        // Step 2.1.2.1: Exclude if not attempted or if attempted and suspended
        if (!child.attemptProgressStatus || (child.attemptCount > 0 && child.isSuspended)) {
          included = false;
        }
      }
      // Step 2.1.3: Check ifAttempted consideration
      else if (
        (rollupAction === "satisfied" && requiredForSatisfied === "ifAttempted") ||
        (rollupAction === "notSatisfied" && requiredForNotSatisfied === "ifAttempted")
      ) {
        // Step 2.1.3.1.1: Exclude if not attempted
        if (!child.attemptProgressStatus || child.attemptCount === 0) {
          included = false;
        }
      }
      // Step 2.1.3.2: Check ifNotSkipped consideration
      else if (
        (rollupAction === "satisfied" && requiredForSatisfied === "ifNotSkipped") ||
        (rollupAction === "notSatisfied" && requiredForNotSatisfied === "ifNotSkipped")
      ) {
        // Step 2.1.3.2.1: Exclude if activity is skipped
        if (child.wasSkipped) {
          included = false;
        }
      }
      // "always" is the default - activity is included
    }

    // RB.1.4.2 Step 3: Check for progress rollup (completed/incomplete)
    if (rollupType === "progress") {
      // Step 3.1: Check if child tracks progress rollup
      if (!child.sequencingControls.rollupProgressCompletion) {
        return false;
      }

      // Step 3.1.1: Default to included
      included = true;

      // Get the child's individual consideration requirements
      const requiredForCompleted = child.requiredForCompleted;
      const requiredForIncomplete = child.requiredForIncomplete;

      // Step 3.1.2: Check ifNotSuspended consideration
      if (
        (rollupAction === "completed" && requiredForCompleted === "ifNotSuspended") ||
        (rollupAction === "incomplete" && requiredForIncomplete === "ifNotSuspended")
      ) {
        // Step 3.1.2.1: Exclude if not attempted or if attempted and suspended
        if (!child.attemptProgressStatus || (child.attemptCount > 0 && child.isSuspended)) {
          included = false;
        }
      }
      // Step 3.1.3: Check ifAttempted consideration
      else if (
        (rollupAction === "completed" && requiredForCompleted === "ifAttempted") ||
        (rollupAction === "incomplete" && requiredForIncomplete === "ifAttempted")
      ) {
        // Step 3.1.3.1.1: Exclude if not attempted
        if (!child.attemptProgressStatus || child.attemptCount === 0) {
          included = false;
        }
      }
      // Step 3.1.3.2: Check ifNotSkipped consideration
      else if (
        (rollupAction === "completed" && requiredForCompleted === "ifNotSkipped") ||
        (rollupAction === "incomplete" && requiredForIncomplete === "ifNotSkipped")
      ) {
        // Step 3.1.3.2.1: Exclude if activity is skipped
        if (child.wasSkipped) {
          included = false;
        }
      }
      // "always" is the default - activity is included
    }

    // Check if child is available for rollup (additional safety check)
    if (included && !child.isAvailable) {
      return false;
    }

    return included;
  }

  /**
   * Filter children based on rollup type and mode
   *
   * @param children - Child activities to filter
   * @param rollupType - Type of rollup ("objective" | "progress")
   * @param mode - Rollup action mode
   * @param considerations - Parent-level rollup considerations config
   * @returns Filtered array of children that should contribute to rollup
   */
  public filterChildrenForRequirement(
    children: Activity[],
    rollupType: "objective" | "progress",
    mode: RollupAction,
    considerations: RollupConsiderationsConfig,
  ): Activity[] {
    return children.filter((child) =>
      this.shouldIncludeChildForRollup(child, rollupType, mode, considerations),
    );
  }

  /**
   * Check if a specific child should be included in rollup
   *
   * @param child - Child activity to check
   * @param rollupType - Type of rollup ("objective" | "progress")
   * @param mode - Rollup action mode
   * @param considerations - Parent-level rollup considerations config
   * @returns True if child should be included
   */
  public shouldIncludeChildForRollup(
    child: Activity,
    rollupType: "objective" | "progress",
    mode: RollupAction,
    considerations: RollupConsiderationsConfig,
  ): boolean {
    // Use the enhanced RB.1.4.2 implementation with rollupAction parameter
    if (!this.checkChildForRollupSubprocess(child, rollupType, mode)) {
      return false;
    }

    // Check parent-level measureSatisfactionIfActive setting
    if (
      rollupType === "objective" &&
      !considerations.measureSatisfactionIfActive &&
      (child.activityAttemptActive || child.isActive)
    ) {
      return false;
    }

    return true;
  }

  /**
   * Check if child is satisfied for rollup
   * Evaluates objective satisfaction status and success status
   *
   * @param child - Child activity to check
   * @returns True if child is considered satisfied
   */
  public isChildSatisfiedForRollup(child: Activity): boolean {
    if (child.objectiveInfoAvailableInCurrentParentAttempt === false) {
      return false;
    }

    if (child.objectiveSatisfiedStatus === true) {
      return true;
    }

    if (child.objectiveSatisfiedStatus === false) {
      return false;
    }

    if (child.successStatus === SuccessStatus.PASSED) {
      return true;
    }

    if (child.successStatus === SuccessStatus.FAILED) {
      return false;
    }

    return false;
  }

  /**
   * Check if a child's objective satisfaction status is actually KNOWN, as opposed
   * to simply defaulting to "not satisfied" because the child was never attempted
   * in this parent attempt.
   *
   * `Activity.objectiveSatisfiedStatus` is a plain boolean that defaults to
   * `false`, so an untouched child and a child that has genuinely failed both
   * read as `false` from {@link isChildSatisfiedForRollup}. The default objective
   * rollup subprocess (RB.1.2.c) must not treat those two cases the same way -
   * an unknown child contributes no information, while a known-not-satisfied
   * child forces the parent to "not satisfied". `objectiveSatisfiedStatusKnown`
   * is the flag the rest of the codebase already treats as authoritative for
   * that distinction (see the identical known/not-known guard in
   * `SequencingStateManager.deserializeActivities`), so it is used here rather
   * than the raw boolean.
   *
   * @spec SN Book: RB.1.4.2 (Check Child For Rollup Subprocess)
   * @spec SN Book: RB.1.2.c (Objective Rollup Using Default) and RB.1.4 (Rollup
   * Rule Check: an unknown status must not evaluate True for either the "any not
   * satisfied" or "all satisfied" default checks)
   * @param child - Child activity to check
   * @returns True if the child's objective satisfaction status is known for this parent attempt
   */
  public isChildObjectiveStatusKnownForRollup(child: Activity): boolean {
    // @spec SCORM 2004 SN 4th Ed. SM.1 useCurrentAttemptObjectiveInfo: when the
    // child's objective data is not visible to the current parent attempt, the
    // parent has no information at all, not a known "not satisfied" status.
    if (child.objectiveInfoAvailableInCurrentParentAttempt === false) {
      return false;
    }

    return child.objectiveSatisfiedStatusKnown;
  }

  /**
   * Check if child is completed for rollup
   * Evaluates completion status
   *
   * @param child - Child activity to check
   * @returns True if child is considered completed
   */
  public isChildCompletedForRollup(child: Activity): boolean {
    if (child.progressInfoAvailableInCurrentParentAttempt === false) {
      return false;
    }

    if (child.completionStatus === "completed" || child.isCompleted) {
      return true;
    }

    return false;
  }

  /**
   * Check if a child's completion status is actually KNOWN, as opposed to
   * defaulting to "incomplete" because the child was never attempted in this
   * parent attempt.
   *
   * Unlike objective satisfaction, `Activity.completionStatus` already has a
   * distinct `CompletionStatus.UNKNOWN` value, so "known" is simply "not
   * unknown" here - but that distinction is lost by
   * {@link isChildCompletedForRollup}, which folds "unknown" and "incomplete"
   * together into `false`. The default progress rollup subprocess (RB.1.3) must
   * not let an untouched sibling's unknown completion status masquerade as a
   * known "incomplete" that forces the parent incomplete.
   *
   * @spec SN Book: RB.1.4.2 (Check Child For Rollup Subprocess)
   * @spec SN Book: RB.1.3 (Activity Progress Rollup Using Default: completed if
   * all children completed, incomplete if any child incomplete - unknown does
   * not count as either) and RB.1.4 (Rollup Rule Check: unknown does not
   * evaluate True for "any")
   * @param child - Child activity to check
   * @returns True if the child's completion status is known for this parent attempt
   */
  public isChildCompletionStatusKnownForRollup(child: Activity): boolean {
    // @spec SCORM 2004 SN 4th Ed. SM.1 useCurrentAttemptProgressInfo: when the
    // child's progress data is not visible to the current parent attempt, the
    // parent has no information at all, not a known "incomplete" status.
    if (child.progressInfoAvailableInCurrentParentAttempt === false) {
      return false;
    }

    return child.completionStatus !== CompletionStatus.UNKNOWN;
  }

  /**
   * Get trackable children for rollup operations
   * Filters out activities with tracked=false from rollup calculations
   *
   * @param activity - The parent activity
   * @returns Array of trackable children
   */
  public getTrackableChildren(activity: Activity): Activity[] {
    return activity.children.filter((child) => child.sequencingControls.tracked !== false);
  }
}
